import { useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Brain, Check, X, Bookmark, ArrowRight, RotateCcw } from "lucide-react";

const haptic = () => navigator.vibrate?.(8);
const ACCENT = "#A78BFA";

const TOPICS = ["", "Patience", "Compassion", "Honesty", "Non-attachment", "Gratitude", "Forgiveness"];

export const WisdomRush = () => {
  const [phase, setPhase] = useState("intro"); // intro | playing | results
  const [topic, setTopic] = useState("");
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [correct, setCorrect] = useState(0);

  const start = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/games/wisdom/round", { mode: topic ? "topic" : "daily", topic: topic || null });
      if (!data.questions?.length) { toast.error("No questions came through. Try again."); return; }
      setQuestions(data.questions);
      setIdx(0); setPicked(null); setCorrect(0);
      setPhase("playing");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  const pick = (i) => {
    if (picked != null) return;
    haptic();
    setPicked(i);
    if (i === questions[idx].answer) setCorrect((c) => c + 1);
  };

  const next = async () => {
    if (idx + 1 < questions.length) { setIdx(idx + 1); setPicked(null); }
    else {
      try { await api.post("/games/wisdom/complete", { correct, total: questions.length }); } catch (e) { /* noop */ }
      setPhase("results");
    }
  };

  const save = async () => {
    const q = questions[idx];
    try { await api.post("/games/wisdom/save", { scenario: q.scenario || "", question: q.question, options: q.options, answer: q.answer, explanation: q.explanation, topic: q.topic }); toast.success("Saved for later reflection."); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (phase === "intro") {
    return (
      <div data-testid="wisdom-intro" className="text-center py-8">
        <Brain className="w-12 h-12 mx-auto mb-4" style={{ color: ACCENT }} />
        <p className="text-slate-300 mb-2 max-w-md mx-auto">Read a short scenario and choose the most skillful response. There&rsquo;s no score of your worth — just a chance to practice discernment.</p>
        <div className="flex flex-wrap justify-center gap-2 my-6">
          {TOPICS.map((t) => (
            <button key={t || "any"} data-testid={`wisdom-topic-${t || "any"}`} onClick={() => setTopic(t)}
              className={`px-3 py-1.5 rounded-full text-xs border transition-colors duration-300 ${topic === t ? "text-slate-900" : "text-slate-300 border-white/15 hover:border-white/40"}`}
              style={topic === t ? { background: ACCENT, borderColor: ACCENT } : {}}>
              {t || "Surprise me"}
            </button>
          ))}
        </div>
        <button data-testid="wisdom-start-btn" onClick={start} disabled={loading}
          className="inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-medium text-slate-900 hover:-translate-y-0.5 transition-transform duration-300 disabled:opacity-60" style={{ background: ACCENT }}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Begin round
        </button>
      </div>
    );
  }

  if (phase === "results") {
    return (
      <div data-testid="wisdom-results" className="text-center py-10">
        <p className="text-sm uppercase tracking-widest text-slate-400 mb-2">Round complete</p>
        <p className="font-head font-900 text-5xl text-slate-50 mb-2">{correct} / {questions.length}</p>
        <p className="text-slate-400 mb-6 text-sm">Thoughtful choices this round. Consistency matters more than perfection.</p>
        <button data-testid="wisdom-again-btn" onClick={() => setPhase("intro")} className="inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-medium text-slate-900" style={{ background: ACCENT }}>
          <RotateCcw className="w-4 h-4" /> Play again
        </button>
      </div>
    );
  }

  const q = questions[idx];
  return (
    <div data-testid="wisdom-playing">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs uppercase tracking-widest" style={{ color: ACCENT }}>{q.topic || "Wisdom"} · {idx + 1}/{questions.length}</span>
        <button data-testid="wisdom-save-btn" onClick={save} className="text-xs text-slate-400 hover:text-slate-200 inline-flex items-center gap-1 transition-colors duration-300"><Bookmark className="w-3.5 h-3.5" /> Save</button>
      </div>
      {q.scenario && <p className="text-slate-400 text-sm mb-2 italic">{q.scenario}</p>}
      <p className="text-lg text-slate-50 font-medium mb-5">{q.question}</p>
      <div className="grid gap-2 mb-5">
        {q.options.map((opt, i) => {
          const isAns = i === q.answer;
          let cls = "border-white/10 text-slate-300 hover:border-white/30";
          if (picked != null && isAns) cls = "border-emerald-400/60 text-emerald-200 bg-emerald-400/10";
          else if (picked === i && !isAns) cls = "border-rose-400/60 text-rose-200 bg-rose-400/10";
          return (
            <button key={i} data-testid={`wisdom-opt-${i}`} onClick={() => pick(i)}
              className={`flex items-center justify-between text-left rounded-xl border px-4 py-3 text-sm transition-colors duration-300 ${cls}`}>
              <span>{opt}</span>
              {picked != null && isAns && <Check className="w-4 h-4" />}
              {picked === i && !isAns && <X className="w-4 h-4" />}
            </button>
          );
        })}
      </div>
      {picked != null && (
        <div className="rounded-xl border p-4 mb-5" style={{ borderColor: `${ACCENT}40`, background: `${ACCENT}0d` }}>
          <p className="text-sm text-slate-200" data-testid="wisdom-explanation">{q.explanation}</p>
        </div>
      )}
      {picked != null && (
        <button data-testid="wisdom-next-btn" onClick={next} className="inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-medium text-slate-900" style={{ background: ACCENT }}>
          {idx + 1 < questions.length ? "Next" : "See results"} <ArrowRight className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};
